// ============================================================================
// cotizar — LA tool crítica (SPEC C3-D5). MOTOR-PRIMERO: el LLM jamás calcula un
// precio; esta función determinista lo hace y devuelve el bloque de texto YA
// formateado que el modelo pega tal cual.
//
// Aplica las reglas_precio del tenant a un precio base y garantiza el invariante
// del bug real del 20-jun (Efectivo < Tarjeta SIEMPRE; jamás iguales).
// Sin dependencias de locale: el formato es determinista.
// ============================================================================

export interface MedioPago {
  nombre: string;
  desc_pct: number; // % de descuento sobre el precio base
}

export interface ReglasPrecio {
  medios_pago: MedioPago[];
  redondeo?: "miles" | "ninguno";
  formato?: string; // "es-CO" (default)
}

export interface PrecioMedio {
  medio: string;
  desc_pct: number;
  precio: number;
  texto: string; // "Efectivo: $2.142.000"
}

export interface Cotizacion {
  /**
   * ★ T-VEN · I-75 · EL PRECIO DE LISTA (el base, sin descuento), redondeado con
   * el MISMO modo que los medios. Siempre ≥ que cualquier medio.
   */
  lista: number;
  medios: PrecioMedio[];
  texto: string; // bloque completo listo para pegar: «Precio de lista: …» y luego los medios
}

/** Formato de pesos colombianos: $2.142.000 (punto de miles, sin decimales). Determinista. */
export function formatearCOP(n: number): string {
  const entero = Math.round(n).toString();
  const conPuntos = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return "$" + conPuntos;
}

/**
 * ⭐ T-DRJ-1 · I-2101 · LA MONEDA VIAJA CON EL ÍTEM (`ProductoCat.moneda?`; ausente = COP).
 * ⛔ COP devuelve EXACTAMENTE `formatearCOP` (Living House byte a byte). ARS → «ARS 80.000»,
 * USD → «USD 150». Una moneda fuera de la lista LANZA: no se adivina un símbolo.
 */
export type Moneda = "COP" | "ARS" | "USD";
export const MONEDAS: readonly Moneda[] = ["COP", "ARS", "USD"];
export function formatearPrecio(n: number, moneda: Moneda = "COP"): string {
  if (moneda === "COP") return formatearCOP(n);
  if (moneda !== "ARS" && moneda !== "USD") throw new Error(`MONEDA_DESCONOCIDA: ${String(moneda)}`);
  return `${moneda} ${formatearCOP(n).slice(1)}`;
}

function redondear(n: number, modo: "miles" | "ninguno"): number {
  if (modo === "miles") return Math.round(n / 1000) * 1000;
  return Math.round(n);
}

/**
 * ⭐ T-DRJ-1 · H7 · ¿el inquilino tiene UN solo precio (un medio al 0 %)? Entonces el
 * texto dice solo «Precio: X»: ni «Precio de lista», ni el nombre del medio. Y un medio
 * pedido que no existe se IGNORA (no hay otro precio que dar): «¿en efectivo?» da el precio.
 * Living House (3 medios) no entra aquí.
 */
export function precioUnico(reglas: ReglasPrecio): boolean {
  return reglas?.medios_pago?.length === 1 && reglas.medios_pago[0].desc_pct === 0;
}

/**
 * Calcula la cotización de un producto/variante para uno o todos los medios de pago.
 * @param precioBase  precio de lista (del conector de catálogo; aquí se recibe ya resuelto)
 * @param reglas      reglas_precio del tenant
 * @param medioPago   opcional; si se omite, devuelve TODOS los medios
 */
export function cotizar(
  precioBase: number,
  reglas: ReglasPrecio,
  medioPago?: string,
  /** ⭐ T-DRJ-1 · la moneda del ítem. Ausente = COP (la salida de siempre). */
  moneda: Moneda = "COP",
): Cotizacion {
  if (!Number.isFinite(precioBase) || precioBase <= 0) {
    throw new Error(`precio base inválido: ${precioBase}`);
  }
  if (!reglas?.medios_pago?.length) {
    throw new Error("reglas_precio sin medios de pago");
  }
  const modo = reglas.redondeo ?? "miles";

  const todos: PrecioMedio[] = reglas.medios_pago.map((m) => {
    if (m.desc_pct < 0 || m.desc_pct >= 100) {
      throw new Error(`descuento inválido para '${m.nombre}': ${m.desc_pct}`);
    }
    const precio = redondear(precioBase * (1 - m.desc_pct / 100), modo);
    return { medio: m.nombre, desc_pct: m.desc_pct, precio, texto: `${m.nombre}: ${formatearPrecio(precio, moneda)}` };
  });

  // MOTOR-PRIMERO (bug 20-jun): distinto descuento ⇒ distinto precio. Si el redondeo
  // colapsa dos medios con descuentos distintos, es config inválida → fallar, no
  // devolver un precio ambiguo que el bot presentaría como "Efectivo = Tarjeta".
  const porDescuento = [...todos].sort((a, b) => a.desc_pct - b.desc_pct);
  for (let i = 1; i < porDescuento.length; i++) {
    const menor = porDescuento[i - 1]; // menor descuento → debe costar más
    const mayor = porDescuento[i];
    if (mayor.desc_pct > menor.desc_pct && mayor.precio >= menor.precio) {
      throw new Error(
        `REGLAS_PRECIO_COLAPSAN: '${mayor.medio}' (${mayor.desc_pct}%) no quedó menor que ` +
          `'${menor.medio}' (${menor.desc_pct}%) tras redondeo — descuentos demasiado cercanos para este precio`,
      );
    }
  }

  let medios = todos;
  if (precioUnico(reglas)) {
    const lista = redondear(precioBase, modo);
    const unico = { ...todos[0], texto: `Precio: ${formatearPrecio(todos[0].precio, moneda)}` };
    return { lista, medios: [unico], texto: unico.texto };
  }
  if (medioPago) {
    const m = todos.find((x) => x.medio.toLowerCase() === medioPago.toLowerCase());
    if (!m) throw new Error(`medio de pago desconocido: ${medioPago}`);
    medios = [m];
  }

  // ★ T-VEN · I-75 · LA LISTA VA PRIMERO, Y LA DICE EL CÓDIGO. Hasta el 23-sep
  // el precio de lista nunca llegaba al modelo, y en la batería del simulador el
  // bot llamó «precio de lista» al de Tarjeta (−10 %): Sala Tuendy L «lista
  // $4.855.000» cuando la lista es ~$5.395.000. Ahora el bloque empieza por el
  // número real. ⛔ No toca los precios por medio ni la guarda de arriba: la
  // lista se calcula aparte, con el mismo redondeo.
  const lista = redondear(precioBase, modo);
  return {
    lista,
    medios,
    texto: [`Precio de lista: ${formatearPrecio(lista, moneda)}`, ...medios.map((m) => m.texto)].join("\n"),
  };
}
